import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Scissors,
  Crown,
  DollarSign,
  Users,
  Calendar,
  Star,
  CheckCircle,
  ArrowRight,
  Smartphone,
  Gift,
  TrendingUp,
  Heart,
  Shield,
  Clock,
  BarChart3,
  Sparkles,
  Play
} from "lucide-react";

const StylistInvitePage = () => {
  const [searchParams] = useSearchParams();
  const referrer = searchParams.get("ref") || "friend";
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const features = [
    {
      icon: DollarSign,
      title: "Keep More Money",
      description: "Flat monthly fee - no 25% cuts like other apps. Your hustle, your profit.",
      highlight: "Save $1,000+/month"
    },
    {
      icon: Calendar,
      title: "Easy Booking",
      description: "Clients book 24/7. No more phone tag, DMs, or missed appointments.",
      highlight: "Automated scheduling"
    },
    {
      icon: Users,
      title: "Build Your Clientele",
      description: "Get discovered by new clients searching for stylists like YOU.",
      highlight: "Priority placement"
    },
    {
      icon: Gift,
      title: "Loyalty Rewards",
      description: "Built-in rewards program keeps clients coming back. Set your own points.",
      highlight: "Increase retention"
    },
    {
      icon: BarChart3,
      title: "Track Your Bag",
      description: "See your revenue, busiest days, and top services. Know your numbers.",
      highlight: "Business insights"
    },
    {
      icon: Smartphone,
      title: "Mobile First",
      description: "Manage everything from your phone. Works where you work.",
      highlight: "Always connected"
    }
  ];

  const testimonials = [
    {
      name: "Marcus J.",
      role: "Barber • Atlanta",
      text: "Finally, an app that gets us. No crazy fees, just simple booking. My clients love it.",
      rating: 5
    },
    {
      name: "Keisha M.",
      role: "Hair Stylist • Houston",
      text: "I was paying StyleSeat 25% of every booking. MyHairChair charges $29/month flat. Do the math. 💰",
      rating: 5
    },
    {
      name: "Carlos R.",
      role: "Barber • Los Angeles",
      text: "The loyalty program alone has increased my repeat clients by 40%. This app is different.",
      rating: 5
    }
  ];

  const pricingComparison = [
    { platform: "StyleSeat", fee: "25% per booking", monthly: "~$500-1,500", type: "cut" },
    { platform: "Vagaro", fee: "$85+/month", monthly: "$85-200", type: "fee" },
    { platform: "Booksy", fee: "$50/month", monthly: "$50+", type: "fee" },
    { platform: "MyHairChair", fee: "$0-29/month", monthly: "$0-29", type: "winner" }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-950 via-stone-900 to-stone-950">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-64 h-64 bg-amber-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-amber-600 rounded-full blur-3xl"></div>
        </div>

        <div className="relative max-w-6xl mx-auto px-4 py-16 md:py-24">
          {/* Beta Badge */}
          <div className={`text-center mb-8 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <Badge className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-2 text-sm">
              🚀 BETA TESTER EXCLUSIVE INVITE
            </Badge>
          </div>

          {/* Main Headline */}
          <div className={`text-center max-w-4xl mx-auto transition-all duration-700 delay-100 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Your Chair. <span className="text-amber-400">Your Business.</span>
              <br />Your Way. 👑
            </h1>
            <p className="text-xl md:text-2xl text-stone-300 mb-4">
              The booking app built <span className="text-amber-400 font-semibold">BY Barbers and Stylists, FOR Barbers and Stylists.</span>
            </p>
            <p className="text-lg text-stone-400 mb-8 max-w-2xl mx-auto">
              Stop giving away 25% of your hard work. Join the movement of Black & Brown beauty professionals taking control of their business.
            </p>
          </div>

          {/* CTA Buttons */}
          <div className={`flex flex-col sm:flex-row items-center justify-center gap-4 mb-12 transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <Link to={`/register?role=stylist&ref=${referrer}&beta=true`}>
              <Button className="h-14 px-8 text-lg bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-black font-bold rounded-full shadow-lg shadow-amber-500/25">
                Join the Beta - It's FREE
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/membership">
              <Button variant="outline" className="h-14 px-8 text-lg border-2 border-amber-500/50 text-amber-400 hover:bg-amber-500/10 rounded-full">
                See Pricing
              </Button>
            </Link>
          </div>

          {/* Trust Badges */}
          <div className={`flex flex-wrap items-center justify-center gap-6 text-stone-400 text-sm transition-all duration-700 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-green-500" />
              <span>No Hidden Fees</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-blue-500" />
              <span>Setup in 5 Minutes</span>
            </div>
            <div className="flex items-center gap-2">
              <Heart className="w-5 h-5 text-red-500" />
              <span>Black Owned</span>
            </div>
          </div>
        </div>
      </section>

      {/* Problem/Solution Section */}
      <section className="py-16 bg-stone-900/50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="bg-red-500/20 text-red-400 mb-4">THE PROBLEM</Badge>
              <h2 className="text-3xl font-bold text-white mb-4">
                Tired of apps taking your money? 💸
              </h2>
              <ul className="space-y-3 text-stone-300">
                <li className="flex items-start gap-3">
                  <span className="text-red-500 mt-1">✗</span>
                  <span>StyleSeat takes <strong>25% of every booking</strong></span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-red-500 mt-1">✗</span>
                  <span>Generic apps don't understand our hair or culture</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-red-500 mt-1">✗</span>
                  <span>Clients can't find you unless you pay for ads</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-red-500 mt-1">✗</span>
                  <span>No-shows and last-minute cancellations kill your day</span>
                </li>
              </ul>
            </div>
            <div>
              <Badge className="bg-green-500/20 text-green-400 mb-4">THE SOLUTION</Badge>
              <h2 className="text-3xl font-bold text-white mb-4">
                MyHairChair is different. 🔥
              </h2>
              <ul className="space-y-3 text-stone-300">
                <li className="flex items-start gap-3">
                  <span className="text-green-500 mt-1">✓</span>
                  <span><strong>Flat monthly fee</strong> - keep what you earn</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-green-500 mt-1">✓</span>
                  <span>Built for Black & Brown beauty professionals</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-green-500 mt-1">✓</span>
                  <span>Get discovered by local clients searching for YOU</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-green-500 mt-1">✓</span>
                  <span>Automated reminders reduce no-shows by 70%</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Comparison */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-12">
            <Badge className="bg-amber-500/20 text-amber-400 mb-4">THE MATH</Badge>
            <h2 className="text-3xl font-bold text-white mb-4">
              See How Much You're Losing 📊
            </h2>
            <p className="text-stone-400">
              If you do 20 bookings/week at $50 average...
            </p>
          </div>

          <div className="grid gap-4">
            {pricingComparison.map((item, idx) => (
              <div 
                key={idx}
                className={`flex items-center justify-between p-4 rounded-xl ${
                  item.type === "winner" 
                    ? "bg-gradient-to-r from-amber-500/20 to-yellow-500/20 border-2 border-amber-500" 
                    : "bg-stone-800/50 border border-stone-700"
                }`}
              >
                <div className="flex items-center gap-4">
                  {item.type === "winner" && <Crown className="w-6 h-6 text-amber-400" />}
                  <div>
                    <p className={`font-bold ${item.type === "winner" ? "text-amber-400" : "text-white"}`}>
                      {item.platform}
                    </p>
                    <p className="text-stone-400 text-sm">{item.fee}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className={`font-bold text-lg ${
                    item.type === "winner" ? "text-green-400" : 
                    item.type === "cut" ? "text-red-400" : "text-stone-300"
                  }`}>
                    {item.monthly}
                  </p>
                  <p className="text-stone-500 text-sm">per month</p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-8 p-6 bg-green-500/10 border border-green-500/30 rounded-xl">
            <p className="text-2xl font-bold text-green-400 mb-2">
              Save up to $1,471/month with MyHairChair 💰
            </p>
            <p className="text-stone-400">That's $17,652/year back in YOUR pocket</p>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-16 bg-stone-900/50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <Badge className="bg-purple-500/20 text-purple-400 mb-4">FEATURES</Badge>
            <h2 className="text-3xl font-bold text-white mb-4">
              Everything You Need to Run Your Business 💼
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, idx) => (
              <Card key={idx} className="bg-stone-800/50 border-stone-700 hover:border-amber-500/50 transition-all">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-yellow-500 rounded-xl flex items-center justify-center mb-4">
                    <feature.icon className="w-6 h-6 text-black" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">{feature.title}</h3>
                  <p className="text-stone-400 mb-3">{feature.description}</p>
                  <Badge className="bg-amber-500/20 text-amber-400">{feature.highlight}</Badge>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <Badge className="bg-pink-500/20 text-pink-400 mb-4">TESTIMONIALS</Badge>
            <h2 className="text-3xl font-bold text-white mb-4">
              What Our Community Says 🗣️
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, idx) => (
              <Card key={idx} className="bg-stone-800/50 border-stone-700">
                <CardContent className="p-6">
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <p className="text-stone-300 mb-4 italic">"{testimonial.text}"</p>
                  <div>
                    <p className="font-bold text-white">{testimonial.name}</p>
                    <p className="text-stone-500 text-sm">{testimonial.role}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Beta CTA Section */}
      <section className="py-16 bg-gradient-to-r from-amber-500/10 to-yellow-500/10">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <Badge className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-2 text-sm mb-6">
            🎉 LIMITED BETA ACCESS
          </Badge>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            You've Been Selected! 👑
          </h2>
          <p className="text-xl text-stone-300 mb-8 max-w-2xl mx-auto">
            As a beta tester, you get <strong className="text-amber-400">FREE access</strong> to all features. 
            Your feedback will help us build the world's greatest booking platform for our community.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
            <Link to={`/register?role=stylist&ref=${referrer}&beta=true`}>
              <Button className="h-16 px-10 text-xl bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-black font-bold rounded-full shadow-lg shadow-amber-500/25">
                <Scissors className="mr-3 w-6 h-6" />
                Start My Free Profile
              </Button>
            </Link>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-6 text-stone-400">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <span>No credit card required</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <span>Free during beta</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <span>Cancel anytime</span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-stone-800">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Scissors className="w-6 h-6 text-amber-500" />
              <span className="text-xl font-bold text-white">MyHairChair</span>
              <Badge className="bg-purple-600 text-white text-xs">BETA</Badge>
            </div>
            <p className="text-stone-500 text-sm">
              © 2025 MyHairChair. Built with ❤️ for beauty professionals.
            </p>
            <div className="flex gap-4">
              <Link to="/login" className="text-stone-400 hover:text-amber-400 transition-colors">
                Login
              </Link>
              <Link to="/register" className="text-stone-400 hover:text-amber-400 transition-colors">
                Sign Up
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default StylistInvitePage;
